import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ActiveFaultCodesWidget,
  Connectivity,
  EngineLoad,
  FuelConsumption,
  PentaDashboard,
  UnitMap,
} from '@volvogroup-internal/dashboard';
import { useTranslation } from 'react-i18next';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { BING_API_KEY } from '@/config/EnviromentConfig';
import { getToken } from '@/config/MsalAuthConfig';
import { analyticsEvents } from '@/contexts/analytics/events';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { client } from '@/service/vpc/vpcService';
import { enviroment } from '@/utils';
import { cn } from '@/utils/cn';

export const Route = createFileRoute('/_authenticated/_account/dashboard/')({
  component: DashboardPage,
  errorComponent: ErrorBoundary,
});

function DashboardPage() {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { isMarineAccount, fleetVerifiedUnits } = useFleetAccount();
  const preFilterUnitsIds = fleetVerifiedUnits.map((unit) => unit.endCustomerUnitId);

  const hasDisplayMapWidget = enviroment.isDev || enviroment.isQA || enviroment.isPreRC ? true : !isMarineAccount;

  return (
    <PentaDashboard
      client={{
        httpClient: {
          instance: client,
          getToken: getToken,
        },
        queryClient,
      }}
      language={i18n.language}
      showFilterBar={true}
      showFilterMenu={true}
      preFilterUnits={preFilterUnitsIds}
    >
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-full flex flex-col items-stretch md:col-span-6">
          <ActiveFaultCodesWidget
            onClick={() => {
              analyticsEvents.Interaction.componentInteraction({
                parent: 'ActiveFaultCodesWidget',
                type: 'ActiveFaultCodesWidget',
                title: 'Active Fault Codes Widget',
                href: '/dashboard/active-fault-codes',
                placement: 'Dashboard',
                selectedOptions: [],
              });
              navigate({ to: '/dashboard/active-fault-codes' });
            }}
          />
        </div>
        <div className="col-span-full flex flex-col items-stretch md:col-span-6">
          <Connectivity
            endCustomerUnitIds={preFilterUnitsIds}
            onClick={() => {
              analyticsEvents.Interaction.componentInteraction({
                parent: 'Connectivity',
                type: 'Connectivity',
                title: 'Connectivity Widget',
                href: '/dashboard/connectivity',
                placement: 'Dashboard',
                selectedOptions: [],
              });
              navigate({ to: '/dashboard/connectivity' });
            }}
          />
        </div>
        <div className="col-span-full flex flex-col items-stretch md:col-span-4">
          <EngineLoad />
        </div>
        {hasDisplayMapWidget && (
          <div className="col-span-full flex flex-col items-stretch md:col-span-8">
            <UnitMap
              defaultZoom={16}
              bingApiKey={BING_API_KEY}
              navigate={(_, endCustomerUnitId) => {
                if (!endCustomerUnitId) return;
                analyticsEvents.Interaction.componentInteraction({
                  parent: 'UnitMap',
                  type: 'UnitMap',
                  title: 'Unit Map Widget',
                  href: '/units/$endCustomerUnitId',
                  placement: 'Dashboard',
                  selectedOptions: [
                    {
                      attribute: 'endCustomerUnitId',
                      value: endCustomerUnitId,
                    },
                  ],
                });
                navigate({
                  to: '/units/$endCustomerUnitId',
                  params: { endCustomerUnitId },
                });
              }}
            />
          </div>
        )}
        <Link
          to="/dashboard/fuel-consumption"
          className={cn('col-span-full flex flex-col items-stretch md:col-span-8', {
            'md:col-span-12': hasDisplayMapWidget,
          })}
        >
          <FuelConsumption type="yearly" />
        </Link>
      </div>
    </PentaDashboard>
  );
}
